import type { FaqOption } from './faq.ts';

/**
 * 자료실 분류. 백엔드 ArchiveCategory enum 과 동일한 값.
 * 구 xcost.me 자료실 게시판 하나가 분류 하나에 대응한다.
 */
export const ARCHIVE_CATEGORIES = [
  'ESTIMATE',
  'STANDARD_ESTIMATE',
  'STANDARD_CODE',
  'MARKET_PRICE',
  'RESOURCE',
  'SHARE',
  'PRECEDENT',
] as const;

export type ArchiveCategory = (typeof ARCHIVE_CATEGORIES)[number];

export const ARCHIVE_CATEGORY_LABEL: Record<ArchiveCategory, string> = {
  ESTIMATE: '적산자료',
  STANDARD_ESTIMATE: '표준품셈·대가기준',
  STANDARD_CODE: '공사표준코드',
  MARKET_PRICE: '표준시장단가',
  RESOURCE: '자원자료',
  SHARE: '공유자료',
  PRECEDENT: '판례',
};

/** 필터 칩·작성 폼 select 순서 (배열 순서 = 화면 순서) */
export const ARCHIVE_CATEGORY_OPTIONS = ARCHIVE_CATEGORIES.map((value) => ({
  value,
  label: ARCHIVE_CATEGORY_LABEL[value],
}));

/**
 * 분류별 하위 분류. 값은 백엔드 archive.constant.ts 와 동일한 코드.
 * 빈 목록인 분류는 하위 분류 없이 저장한다(작성 폼에서 선택 칸이 잠긴다).
 */
export const ARCHIVE_SUB_CATEGORY_OPTIONS = {
  ESTIMATE: [
    { value: 'cost-rate', label: '공사비요율' },
    { value: 'wage', label: '노임단가' },
    { value: 'machine-cost', label: '기계경비' },
    { value: 'pps-price', label: '조달청가격' },
    { value: 'exchange-fuel', label: '환율및유류' },
    { value: 'quality-cost', label: '품질관리비' },
    { value: 'waste', label: '폐기물' },
    { value: 'employment-support', label: '고용개선지원비' },
    { value: 'estimated-price-rule', label: '예정가격작성기준' },
    { value: 'reference-unit-cost', label: '참고일위대가' },
    { value: 'quantity-rule', label: '수량산출기준' },
    { value: 'landscape-tree-price', label: '조경수가격' },
  ],
  STANDARD_ESTIMATE: [
    { value: 'construction', label: '건설공사' },
    { value: 'electrical', label: '전기공사' },
    { value: 'telecom', label: '정보통신' },
    { value: 'fire', label: '소방공사' },
    { value: 'new-tech', label: '건설신기술' },
    { value: 'heritage', label: '문화재수리' },
    { value: 'railway-signal', label: '철도신호' },
    { value: 'disaster', label: '방재분야' },
    { value: 'erosion-control', label: '사방' },
    { value: 'engineering', label: '엔지니어링' },
    { value: 'tree-care', label: '수목진료' },
    { value: 'revision-draft', label: '표준품셈개정안' },
    { value: 'fee-standard', label: '대가기준' },
  ],
  SHARE: [
    { value: 'shared', label: '공유자료' },
    { value: 'lisp', label: 'LISP' },
  ],
  PRECEDENT: [
    { value: 'court', label: '법원' },
    { value: 'constitutional-court', label: '헌법재판소' },
    { value: 'ftc', label: '공정거래위원회' },
    { value: 'arbitration', label: '대한상사중재원' },
  ],
  STANDARD_CODE: [],
  MARKET_PRICE: [],
  RESOURCE: [],
} as const satisfies Record<ArchiveCategory, readonly FaqOption[]>;

export function isArchiveCategory(value: unknown): value is ArchiveCategory {
  return (
    typeof value === 'string' &&
    (ARCHIVE_CATEGORIES as readonly string[]).includes(value)
  );
}

export function getArchiveSubCategoryOptions(
  category: unknown,
): readonly FaqOption[] {
  return isArchiveCategory(category)
    ? ARCHIVE_SUB_CATEGORY_OPTIONS[category]
    : [];
}

/** 분류·하위 분류 조합 검사 — 하위 분류가 없는 분류는 비어 있어야 맞다. */
export function isArchiveSubCategory(
  category: unknown,
  subCategory: unknown,
): boolean {
  const options = getArchiveSubCategoryOptions(category);
  if (options.length === 0) return !subCategory;
  return (
    typeof subCategory === 'string' &&
    options.some((option) => option.value === subCategory)
  );
}

export function getArchiveSubCategoryLabel(
  category: unknown,
  subCategory: unknown,
): string | null {
  if (typeof subCategory !== 'string') return null;
  return (
    getArchiveSubCategoryOptions(category).find(
      (option) => option.value === subCategory,
    )?.label ?? null
  );
}
